import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import DOMPurify from 'dompurify';
import api from '../services/api';
import { BlogPost as BlogPostType, BlogComment } from '../types';
import { extractApiError } from '../utils/errorUtils';
import ImageWithFallback from './ImageWithFallback';

/**
 * BlogPost Component
 *
 * Individual blog post page with comments
 * Issue #1386 - Blog platform frontend
 *
 * Features:
 * - Display blog post content
 * - Rich HTML content rendering
 * - Comment section
 * - Social sharing buttons
 * - SEO-optimized meta tags
 * - View tracking
 */

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<BlogPostType | null>(null);
  const [comments, setComments] = useState<BlogComment[]>([]);
  const [loading, setLoading] = useState(true);
  const [commentFormData, setCommentFormData] = useState({
    content: '',
    author_name: '',
    author_email: ''
  });
  const [submittingComment, setSubmittingComment] = useState(false);
  const [commentCooldown, setCommentCooldown] = useState(false);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);
  const cooldownIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const [user] = useState(() => {
    // Check if user is logged in
    const token = localStorage.getItem('access_token');
    const userData = localStorage.getItem('user');
    return token && userData ? JSON.parse(userData) : null;
  });

  // Fetch post and comments
  useEffect(() => {
    const fetchPost = async () => {
      if (!slug) {
        navigate('/blog');
        return;
      }

      setLoading(true);
      try {
        // Fetch post first, then comments (need post ID)
        const postData = await api.getBlogPost(slug);
        setPost(postData);

        // Fetch comments with post ID
        const postComments = await api.getBlogComments(postData.id);
        setComments(postComments);
      } catch (err: unknown) {
        const apiError = extractApiError(err);
        console.error('Failed to load blog post:', apiError);
        toast.error('Failed to load blog post');
        navigate('/blog');
      } finally {
        setLoading(false);
      }
    };

    fetchPost();
  }, [slug, navigate]);

  // Cleanup interval on unmount (Issue #1398 - prevent memory leak)
  useEffect(() => {
    return () => {
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }
    };
  }, []);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleCommentSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!post) return;

    // Check cooldown
    if (commentCooldown) {
      toast.error(`Please wait ${cooldownSeconds} seconds before commenting again`);
      return;
    }

    // Validation
    if (!commentFormData.content.trim()) {
      toast.error('Please enter a comment');
      return;
    }

    if (!user && (!commentFormData.author_name.trim() || !commentFormData.author_email.trim())) {
      toast.error('Please enter your name and email');
      return;
    }

    setSubmittingComment(true);
    try {
      const newComment = await api.createBlogComment(post.id, {
        content: commentFormData.content,
        author_name: user ? undefined : commentFormData.author_name,
        author_email: user ? undefined : commentFormData.author_email,
        post_id: post.id
      });

      // If authenticated, comment is auto-approved
      if (user) {
        setComments([...comments, newComment]);
        toast.success('Comment posted!');
      } else {
        toast.success('Comment submitted for moderation');
      }

      // Reset form
      setCommentFormData({
        content: '',
        author_name: '',
        author_email: ''
      });

      // Start cooldown (30 seconds)
      // Clear any existing interval first
      if (cooldownIntervalRef.current) {
        clearInterval(cooldownIntervalRef.current);
      }

      setCommentCooldown(true);
      setCooldownSeconds(30);
      cooldownIntervalRef.current = setInterval(() => {
        setCooldownSeconds(prev => {
          if (prev <= 1) {
            if (cooldownIntervalRef.current) {
              clearInterval(cooldownIntervalRef.current);
              cooldownIntervalRef.current = null;
            }
            setCommentCooldown(false);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: unknown) {
      const apiError = extractApiError(err);
      console.error('Failed to post comment:', apiError);
      toast.error('Failed to post comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleShare = (platform: 'facebook' | 'linkedin') => {
    if (!post) return;

    const url = window.location.href;

    let shareUrl = '';
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
        break;
    }

    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-gray-600">Loading post...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return null;
  }

  const approvedComments = comments.filter(c => c.approved);

  return (
    <>
      <Helmet>
        <title>{post.meta_title || post.title} - Local Artist Fairs</title>
        <meta name="description" content={post.meta_description || post.excerpt || post.title} />
        <meta property="og:title" content={post.meta_title || post.title} />
        <meta property="og:description" content={post.meta_description || post.excerpt || post.title} />
        <meta property="og:type" content="article" />
        {post.featured_image_url && <meta property="og:image" content={post.featured_image_url} />}
        {post.published_at && <meta property="article:published_time" content={post.published_at} />}
        {post.author && <meta property="article:author" content={post.author.name || post.author.email} />}
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          {/* Back to Blog */}
          <Link to="/blog" className="inline-flex items-center text-secondary hover:text-primary-dark mb-6">
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Blog
          </Link>

          {/* Article */}
          <article className="bg-white rounded-lg shadow-sm overflow-hidden">
            {/* Featured Image - Issue #1404 with inline SVG placeholder fallback */}
            {post.featured_image_url && (
              <ImageWithFallback
                src={post.featured_image_url}
                alt={post.title}
                loading="lazy"
              />
            )}

            {/* Content */}
            <div className="p-8">
              {/* Categories */}
              {post.categories && post.categories.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-4">
                  {post.categories.map((category) => (
                    <span key={category.id} className="text-sm font-medium text-secondary bg-teal-50 px-3 py-1 rounded-full">
                      {category.name}
                    </span>
                  ))}
                </div>
              )}

              {/* Title */}
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{post.title}</h1>

              {/* Meta */}
              <div className="flex items-center gap-4 text-sm text-gray-600 mb-6 pb-6 border-b border-gray-200">
                {post.published_at && <span>{formatDate(post.published_at)}</span>}
                {post.author && <span>By {post.author.name || post.author.email}</span>}
                {post.view_count !== undefined && post.view_count > 0 && <span>{post.view_count} views</span>}
                {post.comment_count !== undefined && post.comment_count > 0 && <span>{post.comment_count} comments</span>}
              </div>

              {/* Excerpt */}
              {post.excerpt && (
                <div className="text-xl text-gray-700 mb-8 pb-8 border-b border-gray-200 font-medium italic">
                  {post.excerpt}
                </div>
              )}

              {/* Post Content */}
              {/* Security: Defense-in-depth approach - content is sanitized on backend (api/models.py) AND frontend (DOMPurify) */}
              <div
                className="prose prose-lg max-w-none mb-8"
                dangerouslySetInnerHTML={{
                  __html: DOMPurify.sanitize(post.content, {
                    ALLOWED_TAGS: ['p', 'br', 'strong', 'em', 'u', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'ul', 'ol', 'li', 'a', 'img', 'blockquote', 'code', 'pre', 'span', 'div'],
                    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id']
                  })
                }}
              />

              {/* Tags */}
              {post.tags && post.tags.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-8 pb-8 border-b border-gray-200">
                  {post.tags.map((tag) => (
                    <span key={tag.id} className="text-sm text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
                      #{tag.name}
                    </span>
                  ))}
                </div>
              )}

              {/* Social Sharing */}
              <div className="mb-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Share this post</h3>
                <div className="flex gap-3">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z" />
                    </svg>
                    Facebook
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-dark text-white rounded-lg hover:bg-primary-dark transition-colors"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M16 8a6 6 0 016 6v7h-4v-7a2 2 0 00-2-2 2 2 0 00-2 2v7h-4v-7a6 6 0 016-6zM2 9h4v12H2z" />
                      <circle cx="4" cy="4" r="2" />
                    </svg>
                    LinkedIn
                  </button>
                </div>
              </div>
            </div>
          </article>

          {/* Comments Section */}
          <div className="mt-8 bg-white rounded-lg shadow-sm p-8">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Comments ({approvedComments.length})
            </h2>

            {/* Comment Form */}
            <form onSubmit={handleCommentSubmit} className="mb-8 pb-8 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Leave a comment</h3>

              {/* Guest fields */}
              {!user && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <input
                    type="text"
                    placeholder="Your name"
                    value={commentFormData.author_name}
                    onChange={(e) => setCommentFormData({ ...commentFormData, author_name: e.target.value })}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                  <input
                    type="email"
                    placeholder="Your email"
                    value={commentFormData.author_email}
                    onChange={(e) => setCommentFormData({ ...commentFormData, author_email: e.target.value })}
                    className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                    required
                  />
                </div>
              )}

              <textarea
                placeholder="Your comment"
                value={commentFormData.content}
                onChange={(e) => setCommentFormData({ ...commentFormData, content: e.target.value })}
                rows={4}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary mb-4"
                required
              />

              {!user && (
                <p className="text-sm text-gray-600 mb-4">
                  Your comment will be reviewed before being published.
                </p>
              )}

              {/* Cooldown status - accessible to screen readers */}
              {commentCooldown && (
                <div
                  role="status"
                  aria-live="polite"
                  aria-atomic="true"
                  className="sr-only"
                >
                  Comment cooldown active. Please wait {cooldownSeconds} seconds before commenting again.
                </div>
              )}

              <button
                type="submit"
                disabled={submittingComment || commentCooldown}
                aria-label={commentCooldown ? `Please wait ${cooldownSeconds} seconds before commenting again` : submittingComment ? 'Posting comment' : 'Post comment'}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {commentCooldown ? `Wait ${cooldownSeconds}s` : submittingComment ? 'Posting...' : 'Post Comment'}
              </button>
            </form>

            {/* Comments List */}
            {approvedComments.length > 0 ? (
              <div className="space-y-6">
                {approvedComments.map((comment) => (
                  <div key={comment.id} className="border-b border-gray-200 pb-6 last:border-b-0 last:pb-0">
                    <div className="flex items-start gap-4">
                      {/* Avatar */}
                      <div className="flex-shrink-0 w-10 h-10 bg-teal-100 rounded-full flex items-center justify-center">
                        <span className="text-secondary font-medium">
                          {comment.author?.name?.[0] || comment.author_name?.[0] || 'A'}
                        </span>
                      </div>

                      {/* Comment Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-semibold text-gray-900">
                            {comment.author?.name || comment.author_name || 'Anonymous'}
                          </span>
                          <span className="text-sm text-gray-500">
                            {formatDate(comment.created_at)}
                          </span>
                        </div>
                        <p className="text-gray-700 whitespace-pre-wrap">{comment.content}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">
                No comments yet. Be the first to comment!
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default BlogPost;
